import React, { useState, useMemo, useEffect } from "react";
import { Pagination, Select, Space, Typography, Row, Col } from "antd";
import LocationFilters from "../locations/LocationFilters";
import LocationModal from "../locations/LocationModal";
import LocationGrid from "../locations/LocationGrid";
import { useLocations } from "../../hooks/useLocations";

const { Text } = Typography;

const LocationsTab: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterState, setFilterState] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [refreshReasons, setRefreshReasons] = useState<boolean>(false);

  const {
    locations: filteredLocations,
    states,
    loading,
    totalLocations,
  } = useLocations(searchQuery, filterState, refreshReasons);

  const paginatedLocations = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredLocations.slice(startIndex, startIndex + pageSize);
  }, [filteredLocations, currentPage, pageSize, refreshReasons]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterState]);

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: "100%", margin: "16px" }}
    >
      <LocationFilters
        onSearch={setSearchQuery}
        onStateFilter={setFilterState}
        onAddLocation={() => setIsModalOpen(true)}
        states={states}
        totalLocations={totalLocations}
      />

      <LocationGrid locations={paginatedLocations} loading={loading} />

      {totalLocations > 0 && (
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Text type="secondary">
              Showing{" "}
              {Math.min(pageSize * (currentPage - 1) + 1, totalLocations)}-
              {Math.min(pageSize * currentPage, totalLocations)} of{" "}
              {totalLocations} locations
            </Text>
          </Col>
          <Col>
            <Space size="middle">
              <Select
                value={pageSize}
                onChange={setPageSize}
                options={[
                  { label: "9 per page", value: 9 },
                  { label: "12 per page", value: 12 },
                  { label: "24 per page", value: 24 },
                ]}
                style={{ width: 120 }}
              />
              <Pagination
                current={currentPage}
                total={totalLocations}
                pageSize={pageSize}
                onChange={setCurrentPage}
                showSizeChanger={false}
              />
            </Space>
          </Col>
        </Row>
      )}

      <LocationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          setIsModalOpen(false), setRefreshReasons(!refreshReasons);
        }}
      />
    </Space>
  );
};

export default LocationsTab;
